import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaUsers, FaEnvelope, FaHome, FaChevronDown, FaBars, FaTimes, FaHandHoldingHeart } from 'react-icons/fa';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const location = useLocation();

  // Auth state: check if currentUser exists and session is valid
  let userStr = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
  let isLoggedIn = false;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (!user.sessionExpires || new Date(user.sessionExpires) > new Date()) {
        isLoggedIn = true;
      } else {
        localStorage.removeItem('currentUser');
        sessionStorage.removeItem('currentUser');
      }
    } catch (e) {
      localStorage.removeItem('currentUser');
      sessionStorage.removeItem('currentUser');
    }
  }

  // Check if we're on auth pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    if (!isAuthPage) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setIsScrolled(true); // Always show solid background on auth pages
    }
  }, [isAuthPage]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) clearTimeout(dropdownTimeout);
    };
  }, [dropdownTimeout]);

  const handleMouseEnter = useCallback((index) => {
    if (dropdownTimeout) clearTimeout(dropdownTimeout);
    setActiveDropdown(index);
  }, [dropdownTimeout]);

  const handleMouseLeave = useCallback((index) => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 150); // 150ms delay before closing
    setDropdownTimeout(timeout);
  }, []);

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const menuItems = [
    {
      label: 'Home',
      icon: <FaHome />,
      to: '/',
    },
    {
      label: 'Services',
      icon: <FaHandHoldingHeart />,
      onClick: () => scrollToSection('services'),
      dropdown: [
        { 
          label: 'General Medicine', 
          onClick: () => scrollToSection('services'),
          description: 'Primary care and preventive medicine'
        },
        { 
          label: 'Pediatrics', 
          onClick: () => scrollToSection('services'),
          description: 'Specialized care for children and adolescents'
        },
        { 
          label: 'Cardiology', 
          onClick: () => scrollToSection('services'),
          description: 'Heart health and cardiovascular treatments'
        },
        { 
          label: 'Orthopedics', 
          onClick: () => scrollToSection('services'),
          description: 'Musculoskeletal conditions and treatments'
        },
        { 
          label: 'Diagnostic Lab', 
          onClick: () => scrollToSection('services'),
          description: 'Comprehensive testing and analysis'
        },
      ],
    },
    {
      label: 'Our Clinic',
      icon: <FaUsers />,
      onClick: () => scrollToSection('about'),
      dropdown: [
        { 
          label: 'Medical Team', 
          onClick: () => scrollToSection('about'),
          description: 'Meet our experienced physicians'
        },
        { 
          label: 'Patient Stories', 
          onClick: () => scrollToSection('testimonials'),
          description: 'Success stories from our patients'
        },
        { 
          label: 'Facilities', 
          onClick: () => scrollToSection('facilities'),
          description: 'Our state-of-the-art medical center'
        },
      ],
    },
    {
      label: 'Contact',
      icon: <FaEnvelope />,
      onClick: () => scrollToSection('contact'),
    },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled || isAuthPage ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-bold">
              <span className={`${isScrolled || isAuthPage ? 'text-purple-300' : 'text-white'}`}>Health</span>
              <span className={`${isScrolled || isAuthPage ? 'text-purple-600' : 'text-white'}`}>Bridge</span>
              <span className={`${isScrolled || isAuthPage ? 'text-purple-300' : 'text-white'}`}>Clinic</span>
            </h1>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Show menu items only on non-auth pages */}
            {!isAuthPage && (
              <>
                {menuItems.map((item, index) => (
                  <div 
                    key={item.label} 
                    className="relative group"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={() => handleMouseLeave(index)}
                  >
                    {item.to ? (
                      <Link
                        to={item.to}
                        className={`flex items-center space-x-1 hover:text-purple-600 transition ${isScrolled || isAuthPage ? 'text-purple-700' : 'text-white'}`}
                      >
                        {item.icon}
                        <span className="ml-1">{item.label}</span>
                      </Link>
                    ) : (
                      <button
                        onClick={item.onClick}
                        className={`flex items-center space-x-1 hover:text-purple-600 transition ${isScrolled || isAuthPage ? 'text-purple-700' : 'text-white'}`}
                        aria-haspopup={item.dropdown ? 'menu' : undefined}
                        aria-expanded={item.dropdown ? (activeDropdown === index) : undefined}
                      >
                        {item.icon}
                        <span className="ml-1">{item.label}</span>
                        {item.dropdown && (
                          <FaChevronDown className={`ml-1 text-xs transition-transform duration-300 ${
                            activeDropdown === index ? 'rotate-180' : ''
                          }`} />
                        )}
                      </button>
                    )}

                    {/* Dropdown Menu */}
                    {item.dropdown && (
                      <div 
                        className={`absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl py-2 z-50 transition-all duration-300 transform origin-top
                          ${activeDropdown === index 
                            ? 'opacity-100 translate-y-0 visible' 
                            : 'opacity-0 -translate-y-2 invisible'}`}
                      >
                        {item.dropdown.map((subItem) => (
                          <button
                            key={subItem.label}
                            onClick={subItem.onClick}
                            className="w-full px-4 py-3 text-left hover:bg-purple-50 group transition-colors duration-200"
                          >
                            <div className="font-medium text-gray-800 group-hover:text-purple-600">
                              {subItem.label}
                            </div>
                            <div className="text-sm text-gray-500 group-hover:text-purple-500">
                              {subItem.description}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {isLoggedIn ? (
                <>
                  <Link
                    to="/dashboard"
                    className={`px-4 py-2 rounded-full border ${
                      isScrolled || isAuthPage
                        ? 'border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white'
                        : 'border-white text-white hover:bg-white hover:text-purple-600'
                    } transition-colors duration-300`}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      localStorage.removeItem('currentUser');
                      sessionStorage.removeItem('currentUser');
                      localStorage.removeItem('rememberedEmail');
                      window.location.href = '/';
                    }}
                    className="px-4 py-2 rounded-full bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-300"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  {location.pathname !== '/login' && (
                    <Link
                      to="/login"
                      className={`px-4 py-2 rounded-full border ${
                        isScrolled || isAuthPage
                          ? 'border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white'
                          : 'border-white text-white hover:bg-white hover:text-purple-600'
                      } transition-colors duration-300`}
                    >
                      Login
                    </Link>
                  )}
                  {location.pathname !== '/register' && (
                    <Link
                      to="/register"
                      className="px-4 py-2 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors duration-300"
                    >
                      Register
                    </Link>
                  )}
                </>
              )}
            </div>

          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-2xl focus:outline-none z-20"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <FaTimes className={isScrolled || isAuthPage ? 'text-gray-800' : 'text-white'} />
            ) : (
              <FaBars className={isScrolled || isAuthPage ? 'text-gray-800' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-10 bg-black bg-opacity-50">
            <div className="bg-white shadow-lg h-screen w-4/5 max-w-sm overflow-y-auto animate-slideInRight absolute right-0 top-0">
              <div className="px-4 py-6 space-y-4">
                {/* Main navigation links */}
                {menuItems.map((item, index) => (
                  <div key={item.label} className="w-full">
                    {item.to ? (
                      <Link
                        to={item.to}
                        className="flex items-center space-x-2 py-3 w-full text-purple-700 hover:bg-purple-100 rounded-lg px-3"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.icon}
                        <span className="text-sm font-medium">{item.label}</span>
                      </Link>
                    ) : (
                      <>
                        <button
                          className="flex items-center justify-between py-3 w-full text-purple-700 hover:bg-purple-100 rounded-lg px-3 focus:outline-none"
                          onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                          aria-haspopup={item.dropdown ? 'menu' : undefined}
                          aria-expanded={item.dropdown ? (activeDropdown === index) : undefined}
                        >
                          <div className="flex items-center space-x-2">
                            {item.icon}
                            <span className="text-sm font-medium">{item.label}</span>
                          </div>
                          {item.dropdown && (
                            <FaChevronDown className={`text-xs transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                          )}
                        </button>
                        {/* Mobile Dropdown */}
                        {item.dropdown && activeDropdown === index && (
                          <div className="pl-8 py-2 space-y-2 bg-purple-50 rounded-lg mt-1 mb-2">
                            {item.dropdown.map((subItem) => (
                              <button
                                key={subItem.label}
                                onClick={() => {
                                  subItem.onClick();
                                  setIsMobileMenuOpen(false);
                                  setActiveDropdown(null);
                                }}
                                className="block text-left w-full py-2 px-2 rounded hover:bg-purple-100 text-purple-600"
                              >
                                <div className="font-medium text-sm">{subItem.label}</div>
                                <div className="text-xs text-gray-500">{subItem.description}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {/* Auth Buttons */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  {isLoggedIn ? (
                    <>
                      <Link
                        to="/dashboard"
                        className="block w-full px-4 py-3 rounded-full border border-purple-600 text-purple-600 text-center font-semibold hover:bg-purple-600 hover:text-white transition-colors duration-300"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          localStorage.removeItem('currentUser');
                          sessionStorage.removeItem('currentUser');
                          localStorage.removeItem('rememberedEmail');
                          window.location.href = '/';
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full px-4 py-3 rounded-full bg-red-100 text-red-700 hover:bg-red-200 font-semibold transition-colors duration-300"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      {location.pathname !== '/login' && (
                        <Link
                          to="/login"
                          className="block w-full px-4 py-3 rounded-full border border-purple-600 text-purple-600 text-center font-semibold hover:bg-purple-600 hover:text-white transition-colors duration-300"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Login
                        </Link>
                      )}
                      {location.pathname !== '/register' && (
                        <Link
                          to="/register"
                          className="block w-full px-4 py-3 rounded-full bg-purple-600 text-white text-center font-semibold hover:bg-purple-700 transition-colors duration-300"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Register
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;